import { environment } from '../../../../../environments/environment';
import { Injectable } from '@angular/core';
import API, { graphqlOperation, GRAPHQL_AUTH_MODE } from '@aws-amplify/api-graphql';
import { InvoiceData } from '../../dialogs/create-invoice-dialog/create-invoice-dialog.component';

export interface CreatedInvoice {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  clientName: string;
  clientAddress: string;
  clientVatNumber: string;
  clientIdNumber: string;
  subtotal: number;
  totalVat: number;
  grandTotal: number;
  status: 'DRAFT' | 'SENT' | 'PAID' | 'CANCELLED';
  pdfUrl?: string;
  createdAt: string;
  updatedAt: string;
  folderId: string;
  clientID: string;
}

@Injectable({
  providedIn: 'root'
})
export class CreatedInvoiceService {
  
  public readonly IZLAZNE_FAKTURE_FOLDER = 'IZLAZNE FAKTURE';

  constructor() { }

  /**
   * Get or create the IZLAZNE FAKTURE folder at root level
   * This folder is used to store all created invoices
   */
  async getOrCreateIzlazneFaktureFolder(clientId: string): Promise<string> {
    // First, try to find existing IZLAZNE FAKTURE folder at root level
    const listStatement = `
      query ListFoldersByClientID($clientID: ID!, $filter: ModelFolderFilterInput) {
        listFoldersByClientID(clientID: $clientID, filter: $filter) {
          items {
            id
            name
            parentFolderID
          }
        }
      }
    `;

    try {
      const response = (await API.graphql(
        graphqlOperation(listStatement, {
          clientID: clientId,
          filter: {
            name: { eq: this.IZLAZNE_FAKTURE_FOLDER }
          }
        })
      )) as any;

      const existingFolders = response.data.listFoldersByClientID?.items || [];
      
      // Find folder at root level (no parentFolderID or null)
      const rootFolder = existingFolders.find((f: any) => !f.parentFolderID || f.parentFolderID === null);
      
      if (rootFolder) {
        console.log('Found existing IZLAZNE FAKTURE folder:', rootFolder.id);
        return rootFolder.id;
      }

      // Create the folder if it doesn't exist
      return await this.createIzlazneFaktureFolder(clientId);
    } catch (error) {
      console.error('Error finding IZLAZNE FAKTURE folder:', error);
      // Try to create it anyway
      return await this.createIzlazneFaktureFolder(clientId);
    }
  }

  /**
   * Create the IZLAZNE FAKTURE folder at root level
   */
  private async createIzlazneFaktureFolder(clientId: string): Promise<string> {
    const createStatement = `
      mutation CreateFolder($input: CreateFolderInput!) {
        createFolder(input: $input) {
          id
          name
        }
      }
    `;

    try {
      const response = (await API.graphql(
        graphqlOperation(createStatement, {
          input: {
            name: this.IZLAZNE_FAKTURE_FOLDER,
            clientID: clientId,
            status: 'SUCCESS'
          }
        })
      )) as any;

      console.log('Created IZLAZNE FAKTURE folder:', response.data.createFolder.id);
      return response.data.createFolder.id;
    } catch (error) {
      console.error('Error creating IZLAZNE FAKTURE folder:', error);
      throw error;
    }
  }

  /**
   * Create a new invoice and save it directly as an Invoice record
   * This bypasses CreatedInvoice model (which has auth issues) and creates the invoice directly
   */
  async createInvoice(invoiceData: InvoiceData, folderId: string, clientId: string): Promise<any> {
    // Create Invoice record directly - this uses the existing createInvoice mutation that works
    const maxIndex = await this.getMaxInvoiceIndex(folderId);
    
    const createInvoiceStatement = `
      mutation CreateInvoice($input: CreateInvoiceInput!) {
        createInvoice(input: $input) {
          id
          name
          folderID
          clientID
          index
          extractedResults
          title
          createdAt
          updatedAt
        }
      }
    `;

    // Build extractedResults with all invoice data for display and export
    const extractedResults: Record<string, any> = {
      invoiceNumber: invoiceData.invoiceNumber,
      invoiceDate: this.toLocalDateString(invoiceData.invoiceDate),
      dueDate: this.toLocalDateString(invoiceData.dueDate),
      clientName: invoiceData.clientName,
      clientAddress: invoiceData.clientAddress || '',
      clientVatNumber: invoiceData.clientVatNumber || '',
      clientIdNumber: invoiceData.clientIdNumber || '',
      subtotal: invoiceData.subtotal,
      totalVat: invoiceData.totalVat,
      grandTotal: invoiceData.grandTotal,
      lineItems: invoiceData.lineItems,
      notes: invoiceData.notes || '',
      paymentTerms: invoiceData.paymentTerms || '',
      status: 'DRAFT',
      isCreatedInvoice: true
    };

    // Generate a real PDF via the Lambda Function URL so the invoice has a valid S3 URL
    // (without this, the scan handler can't process the invoice)
    let pdfS3Url = `${invoiceData.invoiceNumber}.pdf`; // fallback
    try {
      const pdfResult = await this.convertMemorandum(
        '', // no docxS3Url — Lambda will use reportlab to generate PDF
        clientId,
        invoiceData.invoiceNumber,
        folderId,
        extractedResults
      );
      if (pdfResult.success && pdfResult.pdfS3Url) {
        pdfS3Url = pdfResult.pdfS3Url;
        extractedResults['pdfUrl'] = pdfS3Url;
      }
    } catch (pdfError) {
      console.warn('Could not generate PDF for invoice, using filename fallback:', pdfError);
    }

    const invoiceInput = {
      name: pdfS3Url,
      folderID: folderId,
      clientID: clientId,
      index: maxIndex + 1,
      rotateAngle: 0,
      extractedResults: JSON.stringify(extractedResults),
      title: `Invoice ${invoiceData.invoiceNumber} - ${invoiceData.clientName}`
    };

    try {
      console.log('Creating Invoice with input:', invoiceInput);
      const response = (await API.graphql(
        graphqlOperation(createInvoiceStatement, { input: invoiceInput })
      )) as any;
      
      console.log('Invoice response:', response);
      
      if (response.errors && response.errors.length > 0) {
        console.error('GraphQL errors:', response.errors);
        throw new Error(response.errors[0].message);
      }
      
      const invoice = response.data?.createInvoice;
      
      if (!invoice) {
        console.error('createInvoice returned null. Full response:', JSON.stringify(response));
        throw new Error('Failed to create invoice - no data returned');
      }
      
      console.log('Created Invoice record:', invoice.id);
      return invoice;
    } catch (error: any) {
      console.error('Error creating invoice:', error);
      if (error.errors) {
        console.error('GraphQL error details:', JSON.stringify(error.errors));
      }
      throw error;
    }
  }

  /**
   * Get the maximum index of invoices in a folder
   */
  private async getMaxInvoiceIndex(folderId: string): Promise<number> {
    const statement = `
      query ListInvoicesByFolderID($folderID: ID!) {
        listInvoicesByFolderID(folderID: $folderID, limit: 1, sortDirection: DESC) {
          items {
            index
          }
        }
      }
    `;

    try {
      const response = (await API.graphql(
        graphqlOperation(statement, { folderID: folderId })
      )) as any;
      const items = response.data.listInvoicesByFolderID?.items || [];
      return items.length > 0 ? items[0].index : 0;
    } catch (error) {
      console.error('Error getting max invoice index:', error);
      return 0;
    }
  }

  /**
   * Get all created invoices for a folder
   */
  async getInvoicesByFolder(folderId: string): Promise<CreatedInvoice[]> {
    const statement = `
      query ListCreatedInvoicesByFolderID($folderId: ID!) {
        listCreatedInvoicesByFolderID(folderId: $folderId) {
          items {
            id
            invoiceNumber
            invoiceDate
            dueDate
            clientName
            grandTotal
            status
            pdfUrl
            createdAt
          }
        }
      }
    `;

    try {
      const response = (await API.graphql(
        graphqlOperation(statement, { folderId })
      )) as any;
      return response.data.listCreatedInvoicesByFolderID.items;
    } catch (error) {
      console.error('Error fetching invoices:', error);
      throw error;
    }
  }

  /**
   * Get a single invoice by ID
   */
  async getInvoice(id: string): Promise<CreatedInvoice> {
    const statement = `
      query GetCreatedInvoice($id: ID!) {
        getCreatedInvoice(id: $id) {
          id
          invoiceNumber
          invoiceDate
          dueDate
          clientName
          clientAddress
          clientVatNumber
          clientIdNumber
          subtotal
          totalVat
          grandTotal
          status
          pdfUrl
          lineItems
          notes
          paymentTerms
          createdAt
          updatedAt
          folderId
          clientID
        }
      }
    `;

    try {
      const response = (await API.graphql(
        graphqlOperation(statement, { id })
      )) as any;
      return response.data.getCreatedInvoice;
    } catch (error) {
      console.error('Error fetching invoice:', error);
      throw error;
    }
  }

  /**
   * Update invoice status
   */
  async updateInvoiceStatus(id: string, status: 'DRAFT' | 'SENT' | 'PAID' | 'CANCELLED'): Promise<CreatedInvoice> {
    const statement = `
      mutation UpdateCreatedInvoice($input: UpdateCreatedInvoiceInput!) {
        updateCreatedInvoice(input: $input) {
          id
          status
          updatedAt
        }
      }
    `;

    try {
      const response = (await API.graphql(
        graphqlOperation(statement, { input: { id, status } })
      )) as any;
      return response.data.updateCreatedInvoice;
    } catch (error) {
      console.error('Error updating invoice status:', error);
      throw error;
    }
  }

  /**
   * Generate PDF for an invoice
   */
  async generateInvoicePdf(invoiceId: string): Promise<string> {
    const statement = `
      mutation GenerateInvoicePdf($invoiceId: ID!) {
        generateInvoicePdf(invoiceId: $invoiceId) {
          pdfUrl
          success
        }
      }
    `;

    try {
      const response = (await API.graphql(
        graphqlOperation(statement, { invoiceId })
      )) as any;
      return response.data.generateInvoicePdf.pdfUrl;
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }

  /**
   * Delete an invoice
   */
  async deleteInvoice(id: string): Promise<void> {
    const statement = `
      mutation DeleteCreatedInvoice($input: DeleteCreatedInvoiceInput!) {
        deleteCreatedInvoice(input: $input) {
          id
        }
      }
    `;

    try {
      await API.graphql(
        graphqlOperation(statement, { input: { id } })
      );
    } catch (error) {
      console.error('Error deleting invoice:', error);
      throw error;
    }
  }

  /**
   * Get the next invoice number for a client
   */
  getNextInvoiceNumber(clientId: string): string {
    const year = new Date().getFullYear();
    const storageKey = `lastInvoiceNumber_${clientId}_${year}`;
    const storedLastNumber = localStorage.getItem(storageKey);
    const nextNumber = storedLastNumber ? parseInt(storedLastNumber, 10) + 1 : 1;
    
    // Format: YYYY-NNNN (e.g., 2025-0001)
    return `${year}-${nextNumber.toString().padStart(4, '0')}`;
  }

  /**
   * Get the saved memorandum DOCX S3 URL for a client
   */
  getSavedMemorandumUrl(clientId: string): string | null {
    return localStorage.getItem(`memorandumDocxUrl_${clientId}`);
  }

  /**
   * Get the saved memorandum file name for a client
   */
  getSavedMemorandumFileName(clientId: string): string | null {
    return localStorage.getItem(`memorandumFileName_${clientId}`);
  }

  /**
   * Save the memorandum DOCX S3 URL and file name for a client
   */
  saveMemorandumUrl(clientId: string, url: string, fileName: string): void {
    localStorage.setItem(`memorandumDocxUrl_${clientId}`, url);
    localStorage.setItem(`memorandumFileName_${clientId}`, fileName);
  }

  /**
   * Save the last used invoice number
   */
  saveLastInvoiceNumber(clientId: string, invoiceNumber: string): void {
    const year = new Date().getFullYear();
    const storageKey = `lastInvoiceNumber_${clientId}_${year}`;
    const currentNumber = parseInt(invoiceNumber.split('-')[1], 10);
    localStorage.setItem(storageKey, currentNumber.toString());
  }

  /**
   * Convert a memorandum DOCX file to PDF via the invoicePdfGenerator Lambda
   */
  private readonly LAMBDA_FUNCTION_URL = environment.invoicePdfGeneratorUrl;

  async convertMemorandum(docxS3Url: string, clientId: string, invoiceNumber: string, folderId: string, invoiceData?: any): Promise<{ pdfS3Url: string; success: boolean }> {
    try {
      const response = await fetch(this.LAMBDA_FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          docxS3Url,
          clientId,
          invoiceNumber,
          folderId,
          invoiceData: invoiceData ? JSON.stringify(invoiceData) : undefined
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Lambda invocation failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('Lambda Function URL response:', result);
      return result;
    } catch (error) {
      console.error('Error converting memorandum:', error);
      throw error;
    }
  }

  /**
   * Upload memorandum DOCX, convert to PDF, and create Invoice record in the folder.
   * This is the only method that creates the Invoice record — no separate PDF generation needed.
   * If memorandumFile is null, uses the savedDocxUrl (previously uploaded memorandum).
   */
  async handleMemorandumUpload(
    memorandumFile: File | null,
    folderId: string,
    clientId: string,
    invoiceData: any,
    fileUploadService: any,
    savedDocxUrl?: string
  ): Promise<any> {
    const invoiceNumber = invoiceData.invoiceNumber;

    let docxS3Url: string;
    let memorandumFileName: string;

    if (memorandumFile) {
      // Step 1a: Upload new DOCX to S3 via presigned URL
      console.log('Uploading new memorandum DOCX to S3...');
      docxS3Url = await fileUploadService.getPresignedUrl(memorandumFile);
      memorandumFileName = memorandumFile.name;
      console.log('Memorandum DOCX uploaded to:', docxS3Url);

      // Save for future reuse
      this.saveMemorandumUrl(clientId, docxS3Url, memorandumFileName);
    } else if (savedDocxUrl) {
      // Step 1b: Reuse previously uploaded memorandum
      console.log('Reusing saved memorandum DOCX:', savedDocxUrl);
      docxS3Url = savedDocxUrl;
      memorandumFileName = this.getSavedMemorandumFileName(clientId) || 'memorandum.docx';
    } else {
      throw new Error('No memorandum file provided and no saved memorandum found');
    }

    // Step 2: Call Lambda to fill DOCX template with invoice data, then convert to PDF
    console.log('Converting memorandum to PDF with invoice data...');
    const invoiceDataForLambda = {
      invoiceNumber: invoiceData.invoiceNumber,
      invoiceDate: invoiceData.invoiceDate instanceof Date ? this.toLocalDateString(invoiceData.invoiceDate) : invoiceData.invoiceDate,
      dueDate: invoiceData.dueDate instanceof Date ? this.toLocalDateString(invoiceData.dueDate) : invoiceData.dueDate,
      clientName: invoiceData.clientName,
      clientAddress: invoiceData.clientAddress || '',
      clientVatNumber: invoiceData.clientVatNumber || '',
      clientIdNumber: invoiceData.clientIdNumber || '',
      subtotal: invoiceData.subtotal,
      totalVat: invoiceData.totalVat,
      grandTotal: invoiceData.grandTotal,
      lineItems: invoiceData.lineItems,
      notes: invoiceData.notes || '',
      paymentTerms: invoiceData.paymentTerms || ''
    };
    const conversionResult = await this.convertMemorandum(docxS3Url, clientId, invoiceNumber, folderId, invoiceDataForLambda);
    console.log('Conversion result:', conversionResult);

    if (!conversionResult.success) {
      throw new Error('Memorandum conversion failed');
    }

    // Step 3: Create a single Invoice record with the converted PDF and invoice metadata
    const maxIndex = await this.getMaxInvoiceIndex(folderId);
    const createInvoiceStatement = `
      mutation CreateInvoice($input: CreateInvoiceInput!) {
        createInvoice(input: $input) {
          id
          name
          folderID
          clientID
          index
          title
          createdAt
        }
      }
    `;

    const extractedResults = {
      invoiceNumber: invoiceNumber,
      invoiceDate: invoiceData.invoiceDate instanceof Date ? this.toLocalDateString(invoiceData.invoiceDate) : invoiceData.invoiceDate,
      dueDate: invoiceData.dueDate instanceof Date ? this.toLocalDateString(invoiceData.dueDate) : invoiceData.dueDate,
      clientName: invoiceData.clientName,
      clientAddress: invoiceData.clientAddress || '',
      clientVatNumber: invoiceData.clientVatNumber || '',
      clientIdNumber: invoiceData.clientIdNumber || '',
      subtotal: invoiceData.subtotal,
      totalVat: invoiceData.totalVat,
      grandTotal: invoiceData.grandTotal,
      lineItems: invoiceData.lineItems,
      notes: invoiceData.notes || '',
      paymentTerms: invoiceData.paymentTerms || '',
      status: 'DRAFT',
      isCreatedInvoice: true,
      memorandumDocxUrl: docxS3Url,
      pdfUrl: conversionResult.pdfS3Url
    };

    const invoiceInput = {
      name: conversionResult.pdfS3Url,
      folderID: folderId,
      clientID: clientId,
      index: maxIndex + 1,
      rotateAngle: 0,
      title: `Invoice ${invoiceNumber} - ${invoiceData.clientName}`,
      extractedResults: JSON.stringify(extractedResults)
    };

    const response = (await API.graphql(
      graphqlOperation(createInvoiceStatement, { input: invoiceInput })
    )) as any;

    // Save the last used invoice number
    this.saveLastInvoiceNumber(clientId, invoiceNumber);

    // NOTE: Do NOT manually update invoicesCount here.
    // The backend pipeline resolver (Mutation.incrementInvoiceCounter.req.vtl)
    // already atomically increments invoicesCount on createInvoice.
    // Manually overwriting it with maxIndex+1 caused exponential count growth
    // because index = (invoicesCount+1)*10, so the count was multiplied ~10x each time.

    console.log('Created Invoice record:', response.data?.createInvoice?.id);
    return response.data?.createInvoice;
  }

  /**
   * Update folder invoicesCount to enable the Run button
   */
  private async updateFolderInvoicesCount(folderId: string, count: number): Promise<void> {
    const statement = `
      mutation UpdateFolder($input: UpdateFolderInput!) {
        updateFolder(input: $input) {
          id
          invoicesCount
        }
      }
    `;

    try {
      await API.graphql(
        graphqlOperation(statement, {
          input: {
            id: folderId,
            invoicesCount: count
          }
        })
      );
      console.log(`Updated folder ${folderId} invoicesCount to ${count}`);
    } catch (error) {
      console.error('Error updating folder invoicesCount:', error);
    }
  }

  /**
   * Push invoice data to EPO and KPR Google Sheets
   */
  private toLocalDateString(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  async pushToGoogleSheets(invoiceId: string): Promise<{ epoSuccess: boolean; kprSuccess: boolean }> {
    const statement = `
      mutation PushInvoiceToSheets($invoiceId: ID!) {
        pushInvoiceToSheets(invoiceId: $invoiceId) {
          epoSuccess
          kprSuccess
        }
      }
    `;

    try {
      const response = (await API.graphql(
        graphqlOperation(statement, { invoiceId })
      )) as any;
      return response.data.pushInvoiceToSheets;
    } catch (error) {
      console.error('Error pushing to Google Sheets:', error);
      throw error;
    }
  }
}
